import { AppPlugins, helpUrlDefinition, pluginRoutePrefix } from 'app/constants'
import { IHostsListPageTabs } from 'app/plugins/openstack/components/infrastructure/hosts/constants'
import {
  IHealthServiceDetailsPageTabs,
  ILoadBalancerDetailsPageTabs,
} from 'app/plugins/openstack/components/networks/model'
import { IVirtualMachinesListPageTabs } from 'app/plugins/openstack/components/vms/VirtualMachinesListPage'
import { Route } from 'core/plugins/route'
import { ImageDetailsPageTabs } from 'openstack/components/images/details/model'
import { ImagePageTabs } from 'openstack/components/images/model'
import { IGpuPageTabs } from 'openstack/components/infrastructure/gpu/model'
import { IHostDetailsPageTabs } from 'openstack/components/infrastructure/hosts/host-details/model'
import { INetworkDetailsPageTabs } from 'openstack/components/networks/network-details/model'
import { IRouterDetailsPageTabs } from 'openstack/components/networks/routers/model'
import {
  IAppsDetailsPageTabs,
  IStackDetailsPageTabs,
} from 'openstack/components/orchestration/model'
import { IVolumeSnapshotDetailsPageTabs } from 'openstack/components/storage/snapshots/details/model'
import { IVolumeTypeDetailsPageTabs } from 'openstack/components/storage/volume-types/details/model'
import { IVolumeDetailsPageTabs } from 'openstack/components/storage/volumes/volume-details/model'
import { IServerGroupDetailsPageTabs } from 'openstack/components/vms/server-groups/details/model'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'

const metadata = {
  showGlobalFilters: true,
}

export const createOpenstackRoutes = (plugin: string = AppPlugins.OpenStack) => {
  const defaultParams = {
    plugin,
  }

  return {
    openstack: {
      dashboard: Route.register({
        url: `${pluginRoutePrefix}/dashboard`,
        name: 'Dashboard',
        id: 'openstackDashboard',
        breadcrumbs: new Map([['dashboard', 'openstackDashboard']]),
        metadata,
        defaultParams: {
          ...defaultParams,
        },
      }),
      images: Route.register({
        url: `${pluginRoutePrefix}/images/:tab`,
        name: 'Images and VM Snapshots',
        id: 'openstackImages',
        breadcrumbs: new Map([['Images and VM Snapshots', 'openstackImages']]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: ImagePageTabs.Images,
        },
        tab: 'tab',
      }),
      addImage: Route.register({
        url: `${pluginRoutePrefix}/images/:tab/add`,
        name: 'Images and VM Snapshots:Add',
        breadcrumbs: new Map([
          ['Images and VM Snapshots', 'openstackImages'],
          ['add', 'openstackImages'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: ImagePageTabs.Images,
        },
        tab: 'tab',
      }),
      importImage: Route.register({
        url: `${pluginRoutePrefix}/images/:tab/import`,
        name: 'Images and VM Snapshots:Import',
        breadcrumbs: new Map([
          ['Images and VM Snapshots', 'openstackImages'],
          ['import', 'openstackImages'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: ImagePageTabs.Images,
        },
        tab: 'tab',
      }),
      imageDetails: Route.register<{
        id: string
        detailsTab?: ImageDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/images/:id/:detailsTab`,
        name: 'Images and VM Snapshots:Details',
        id: 'openstackImageDetails',
        breadcrumbs: new Map([
          ['Images and VM Snapshots', 'openstackImages'],
          [':id', 'openstackImageDetails'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
          detailsTab: ImageDetailsPageTabs.Overview,
        },
        tab: 'detailsTab',
      }),
      vms: Route.register<{
        vmstab?: IVirtualMachinesListPageTabs
      }>({
        url: `${pluginRoutePrefix}/compute/vms/:vmstab`,
        name: 'Virtual Machines',
        id: 'openstackVms',
        breadcrumbs: new Map([['Virtual Machines', 'openstackVms']]),
        metadata,
        defaultParams: {
          ...defaultParams,
          vmstab: IVirtualMachinesListPageTabs.VirtualMachinesList,
        },
        tab: 'vmstab',
      }),
      vmDetails: Route.register<{
        id: string
        tab?: IVmDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/compute/vms/${IVirtualMachinesListPageTabs.VirtualMachinesList}/:id/:tab`,
        name: 'VirtualMachines:Details',
        id: 'openstackVmDetails',
        breadcrumbs: new Map([
          ['Virtual Machines', 'openstackVms'],
          [':id', 'openstackVmDetails'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'overview',
        },
        tab: 'tab',
      }),
      deployVm: Route.register({
        url: `${pluginRoutePrefix}/compute/vms/${IVirtualMachinesListPageTabs.VirtualMachinesList}/deploy`,
        name: 'OpenstackVms:Deploy',
        breadcrumbs: new Map([
          ['Virtual Machines', 'openstackVms'],
          ['deploy', 'openstackVms'],
        ]),
        metadata,
        defaultParams,
      }),
      serverGroups: Route.register({
        url: `${pluginRoutePrefix}/compute/server-groups`,
        name: 'Server Groups',
        id: 'serverGroups',
        breadcrumbs: new Map([['Server Groups', 'serverGroups']]),
        metadata,
        defaultParams,
      }),
      createServerGroup: Route.register({
        url: `${pluginRoutePrefix}/compute/server-groups/create`,
        name: 'Server Groups:Create',
        breadcrumbs: new Map([
          ['Server Groups', 'serverGroups'],
          ['create', 'serverGroups'],
        ]),
        metadata,
        defaultParams,
      }),
      serverGroupDetails: Route.register<{
        id: string
        tab?: IServerGroupDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/compute/server-groups/:id/:tab`,
        name: 'ServerGroups:Details',
        id: 'serverGroupDetails',
        breadcrumbs: new Map([
          ['Server Groups', 'serverGroups'],
          [':id', 'serverGroupDetails'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'overview',
        },
        tab: 'tab',
      }),
      flavors: Route.register({
        url: `${pluginRoutePrefix}/flavors`,
        name: 'Flavors',
        id: 'openstackFlavors',
        breadcrumbs: new Map([['flavors', 'openstackFlavors']]),
        metadata,
        defaultParams,
      }),
      createFlavor: Route.register({
        url: `${pluginRoutePrefix}/flavors/create`,
        name: 'OpenstackFlavors:Create',
        breadcrumbs: new Map([
          ['Flavors', 'openstackFlavors'],
          ['create', 'openstackFlavors'],
        ]),
        metadata,
        defaultParams,
      }),
      sshKeys: Route.register({
        url: `${pluginRoutePrefix}/ssh-key`,
        name: 'SSH Keys',
        id: 'ssh-key',
        breadcrumbs: new Map([['SSH Keys', 'ssh-key']]),
        metadata,
        defaultParams,
      }),
      importSshKey: Route.register({
        url: `${pluginRoutePrefix}/ssh-key/import`,
        name: 'ssh-key:Import',
        breadcrumbs: new Map([
          ['SSH Keys', 'ssh-key'],
          ['Import SSH Key', 'ssh-key'],
        ]),
        metadata,
        defaultParams,
      }),
      storageDashboard: Route.register({
        url: `${pluginRoutePrefix}/storage/dashboard`,
        name: 'Storage Dashboard',
        id: 'storageDashboard',
        breadcrumbs: new Map([['dashboard', 'storageDashboard']]),
        metadata,
        defaultParams,
      }),
      volumes: Route.register({
        url: `${pluginRoutePrefix}/storage/volumes`,
        name: 'Volumes',
        id: 'openstackVolumes',
        breadcrumbs: new Map([['volumes', 'openstackVolumes']]),
        metadata,
        defaultParams,
      }),
      volumeDetails: Route.register<{
        id: string
        tab?: IVolumeDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/storage/volumes/:id/:tab`,
        name: 'Volumes:Details',
        id: 'openstackVolumeDetails',
        breadcrumbs: new Map([
          ['volumes', 'openstackVolumes'],
          [':id', 'openstackVolumeDetails'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'overview',
        },
        tab: 'tab',
      }),
      createVolume: Route.register({
        url: `${pluginRoutePrefix}/storage/volumes/create`,
        name: 'OpenstackVolumes:Create',
        breadcrumbs: new Map([
          ['volumes', 'openstackVolumes'],
          ['create', 'openstackVolumes'],
        ]),
        metadata,
        defaultParams,
      }),
      volumeTypes: Route.register({
        url: `${pluginRoutePrefix}/storage/volume-types`,
        name: 'VolumeTypes',
        id: 'volumeTypes',
        breadcrumbs: new Map([['volume-types', 'volumeTypes']]),
        metadata,
        defaultParams,
      }),
      createVolumeType: Route.register({
        url: `${pluginRoutePrefix}/storage/volume-types/create`,
        name: 'VolumeTypes:Create',
        breadcrumbs: new Map([
          ['volume-types', 'volumeTypes'],
          ['create', 'volumeTypes'],
        ]),
        metadata,
        defaultParams,
      }),
      volumeTypeDetails: Route.register<{
        id: string
        tab?: IVolumeTypeDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/storage/volume-types/:id/:tab`,
        name: 'VolumeTypes:Details',
        id: 'volumeTypeDetails',
        breadcrumbs: new Map([
          ['volume-types', 'volumeTypes'],
          [':id', 'volumeTypeDetails'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'overview',
        },
        tab: 'tab',
      }),
      volumeSnapshots: Route.register({
        url: `${pluginRoutePrefix}/storage/snapshots`,
        name: 'Volume Snapshots',
        id: 'openstackVolumeSnapshots',
        breadcrumbs: new Map([['volume-snapshots', 'openstackVolumeSnapshots']]),
        metadata,
        defaultParams,
      }),
      volumeSnapshotDetails: Route.register<{
        id: string
        tab?: IVolumeSnapshotDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/storage/snapshots/:id/:tab`,
        name: 'VolumeSnapshots:Details',
        id: 'openstackVolumeSnapshotDetails',
        breadcrumbs: new Map([
          ['volume-snapshots', 'openstackVolumeSnapshots'],
          [':id', 'openstackVolumeSnapshotDetails'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'overview',
        },
        tab: 'tab',
      }),
      networks: Route.register({
        url: `${pluginRoutePrefix}/networking/networks`,
        name: 'Networks',
        id: 'openstackNetworks',
        breadcrumbs: new Map([['Networks', 'openstackNetworks']]),
        metadata,
        defaultParams,
      }),
      networkDetails: Route.register<{
        id: string
        tab?: INetworkDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/networking/networks/:id/:tab`,
        name: 'Networks:Details',
        id: 'openstackNetworkDetails',
        breadcrumbs: new Map([
          ['networks', 'openstackNetworks'],
          [':id', 'openstackNetworkDetails'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'overview',
        },
        tab: 'tab',
      }),
      createNetwork: Route.register({
        url: `${pluginRoutePrefix}/networking/networks/create`,
        name: 'OpenstackNetworks:CreateNetwork',
        breadcrumbs: new Map([
          ['networks', 'openstackNetworks'],
          ['create', 'openstackCreateNetwork'],
        ]),
        metadata,
        defaultParams,
      }),
      createPhysicalNetwork: Route.register({
        url: `${pluginRoutePrefix}/networking/networks/create-physical-network`,
        name: 'OpenstackNetworks:CreatePhysicalNetwork',
        breadcrumbs: new Map([
          ['networks', 'openstackNetworks'],
          ['create physical network', 'openstackNetworks'],
        ]),
        metadata,
        defaultParams,
      }),
      createVirtualNetwork: Route.register({
        url: `${pluginRoutePrefix}/networking/networks/create-virtual-network`,
        name: 'OpenstackNetworks:CreateVirtualNetwork',
        breadcrumbs: new Map([
          ['networks', 'openstackNetworks'],
          ['create virtual network', 'openstackNetworks'],
        ]),
        metadata,
        defaultParams,
      }),
      createNetworkSubnet: Route.register({
        url: `${pluginRoutePrefix}/networking/networks/:id/subnets/create`,
        name: 'OpenstackSubnets:CreateNetworkSubnet',
        breadcrumbs: new Map([
          ['networks', 'openstackNetworks'],
          [':id', 'openstackNetworkDetails'],
        ]),
        metadata,
        defaultParams,
      }),
      createNetworkPort: Route.register({
        url: `${pluginRoutePrefix}/networking/networks/:id/ports/create`,
        name: 'OpenstackSubnets:CreateNetworkPort',
        breadcrumbs: new Map([
          ['networks', 'openstackNetworks'],
          [':id', 'openstackNetworkDetails'],
        ]),
        metadata,
        defaultParams,
      }),
      // virtualNetworks: Route.register({
      //   url: `${pluginRoutePrefix}/networking/virtual-networks`,
      //   name: 'VirtualNetworks',
      //   id: 'virtualNetworks',
      //   breadcrumbs: new Map([['virtual-networks', 'virtualNetworks']]),
      //   metadata,
      //   defaultParams,
      // }),
      // virtualNetworkDetails: Route.register<{
      //   id: string
      //   tab?: INetworkDetailsPageTabs
      // }>({
      //   url: `${pluginRoutePrefix}/networking/virtual-networks/:id/:tab`,
      //   name: 'VirtualNetworks:Details',
      //   id: 'virtualNetworkDetails',
      //   breadcrumbs: new Map([
      //     ['virtual-networks', 'virtualNetworks'],
      //     [':id', 'virtualNetworkDetails'],
      //   ]),
      //   metadata,
      //   defaultParams: {
      //     ...defaultParams,
      //     tab: 'overview',
      //   },
      //   tab: 'tab',
      // }),
      // createVirtualNetwork: Route.register({
      //   url: `${pluginRoutePrefix}/networking/virtual-networks/create`,
      //   name: 'VirtualNetworks:Create',
      //   breadcrumbs: new Map([
      //     ['virtual-networks', 'virtualNetworks'],
      //     ['create', 'virtualNetworks'],
      //   ]),
      //   metadata,
      //   defaultParams,
      // }),
      vmwareNetworks: Route.register({
        url: `${pluginRoutePrefix}/networking/vmw-networks`,
        name: 'VmwNetworks',
        id: 'openstackVmwNetworks',
        breadcrumbs: new Map([['networks', 'openstackVmwNetworks']]),
        metadata,
        defaultParams,
      }),
      vmwareNetworkDetails: Route.register<{
        id: string
        tab?: INetworkDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/networking/vmw-networks/:id/:tab`,
        name: 'VmwNetworks:Details',
        id: 'openstackVmwNetworkDetails',
        breadcrumbs: new Map([
          ['networks', 'openstackVmwNetworks'],
          [':id', 'openstackVmwNetworkDetails'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'overview',
        },
        tab: 'tab',
      }),
      createVmwareNetwork: Route.register({
        url: `${pluginRoutePrefix}/networking/vmw-networks/create`,
        name: 'OpenstackVmwNetworks:Create',
        breadcrumbs: new Map([
          ['networks', 'openstackVmwNetworks'],
          ['create', 'openstackVmwNetworks'],
        ]),
        metadata,
        defaultParams,
      }),
      createVirtualNetworkSubnet: Route.register({
        url: `${pluginRoutePrefix}/networking/virtual-networks/:id/subnets/create`,
        name: 'OpenstackSubnets:CreateVirtualNetworkSubnet',
        breadcrumbs: new Map([
          ['virtual-networks', 'virtualNetworks'],
          [':id', 'virtualNetworkDetails'],
        ]),
        metadata,
        defaultParams,
      }),
      createVmwSubnet: Route.register({
        url: `${pluginRoutePrefix}/networking/vmw-networks/:id/subnets/create`,
        name: 'OpenstackVmwSubnets:CreateVmwSubnet',
        breadcrumbs: new Map([
          ['networks', 'openstackVmwNetworks'],
          [':id', 'openstackVmwNetworkDetails'],
        ]),
        metadata,
        defaultParams,
      }),
      createVirtualNetworkPort: Route.register({
        url: `${pluginRoutePrefix}/networking/virtual-networks/:id/ports/create`,
        name: 'OpenstackPorts:CreateVirtualNetworkPort',
        breadcrumbs: new Map([
          ['virtual-networks', 'virtualNetworks'],
          [':id', 'virtualNetworkDetails'],
        ]),
        metadata,
        defaultParams,
      }),
      createVmwPort: Route.register({
        url: `${pluginRoutePrefix}/networking/vmw-networks/:id/ports/create`,
        name: 'OpenstackVmwPorts:CreateVmwPort',
        breadcrumbs: new Map([
          ['networks', 'openstackVmwNetworks'],
          [':id', 'openstackVmwNetworkDetails'],
        ]),
        metadata,
        defaultParams,
      }),
      routers: Route.register({
        url: `${pluginRoutePrefix}/networking/routers`,
        name: 'Routers',
        id: 'openstackRouters',
        breadcrumbs: new Map([['routers', 'openstackRouters']]),
        metadata,
        defaultParams,
      }),
      routerDetails: Route.register<{
        id: string
        tab?: IRouterDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/networking/routers/:id/:tab`,
        name: 'Routers:Details',
        id: 'routerDetails',
        breadcrumbs: new Map([
          ['routers', 'openstackRouters'],
          [':id', 'routerDetails'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'overview',
        },
        tab: 'tab',
      }),
      createRouter: Route.register({
        url: `${pluginRoutePrefix}/networking/routers/create`,
        name: 'OpenstackRouters:Create',
        breadcrumbs: new Map([
          ['routers', 'openstackRouters'],
          ['create', 'openstackRouters'],
        ]),
        metadata,
        defaultParams,
      }),
      createRouterInterface: Route.register({
        url: `${pluginRoutePrefix}/networking/routers/:id/interfaces/create`,
        name: 'OpenstackRouterInterface:Create',
        breadcrumbs: new Map([
          ['routers', 'openstackRouters'],
          [':id', 'routerDetails'],
        ]),
        metadata,
        defaultParams,
      }),
      clusterBlueprint: Route.register({
        url: `${pluginRoutePrefix}/infrastructure/blueprint`,
        name: 'ClusterBlueprint',
        id: 'clusterBlueprint',
        breadcrumbs: new Map([['blueprint', 'clusterBlueprint']]),
        metadata,
        defaultParams,
      }),
      clusterServerRoles: Route.register({
        url: `${pluginRoutePrefix}/infrastructure/host-roles`,
        name: 'ClusterHostRoles',
        id: 'clusterHostRoles',
        breadcrumbs: new Map([['host-roles', 'clusterHostRoles']]),
        metadata,
        defaultParams,
      }),
      hosts: Route.register<{
        hoststab?: IHostsListPageTabs
      }>({
        url: `${pluginRoutePrefix}/infrastructure/hosts/:hoststab`,
        name: 'Cluster Hosts',
        id: 'openstackHosts',
        breadcrumbs: new Map([['cluster-hosts', 'openstackHosts']]),
        metadata,
        defaultParams: {
          ...defaultParams,
          hoststab: IHostsListPageTabs.HostsList,
        },
        tab: 'hoststab',
      }),
      hostDetails: Route.register<{
        id: string
        tab?: IHostDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/infrastructure/hosts/${IHostsListPageTabs.HostsList}/:id/:tab`,
        name: 'ClusterHosts:Details',
        id: 'hostDetails',
        breadcrumbs: new Map([
          ['cluster-hosts', 'openstackHosts'],
          [':id', 'hostDetails'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'overview',
        },
        tab: 'tab',
      }),
      addHosts: Route.register({
        url: `${pluginRoutePrefix}/infrastructure/hosts/${IHostsListPageTabs.HostsList}/add`,
        name: 'Add Cluster Hosts',
        id: 'addOpenstackHosts',
        breadcrumbs: new Map([
          ['cluster-hosts', 'openstackHosts'],
          ['Add a New Host', 'addOpenstackHosts'],
        ]),
        metadata,
        defaultParams,
      }),
      addVmwareGateway: Route.register({
        url: `${pluginRoutePrefix}/infrastructure/hosts/${IHostsListPageTabs.HostsList}/add-gateway`,
        name: 'Add VMware Gateway',
        id: 'addVmwareGateway',
        breadcrumbs: new Map([['cluster-hosts', 'vmwareGateway']]),
        metadata,
        defaultParams,
      }),
      hostAggregates: Route.register({
        url: `${pluginRoutePrefix}/infrastructure/host-aggregates`,
        name: 'Host Aggregates',
        id: 'openstackHostAggregates',
        breadcrumbs: new Map([['host-aggregates', 'openstackHostAggregates']]),
        metadata,
        defaultParams,
      }),
      createHostAggregate: Route.register({
        url: `${pluginRoutePrefix}/infrastructure/host-aggregates/create`,
        name: 'Add Host Aggregate',
        id: 'createHostAggregate',
        breadcrumbs: new Map([['host-aggregates', 'openstackHostAggregates']]),
        metadata,
        defaultParams,
      }),
      stretchedClusters: Route.register({
        url: `${pluginRoutePrefix}/infrastructure/stretched-cluster`,
        name: 'Stretched Clusters',
        id: 'stretchedClusters',
        breadcrumbs: new Map([['stretched-cluster', 'stretchedClusters']]),
        metadata,
        defaultParams,
      }),
      stretchedClusterDetails: Route.register<{ id: string; tab?: string }>({
        url: `${pluginRoutePrefix}/infrastructure/stretched-cluster/:id/:tab`,
        name: 'Stretched Cluster Details',
        id: 'stretchedClusterDetails',
        breadcrumbs: new Map([
          ['stretched-cluster', 'stretchedClusters'],
          [':id', 'stretchedClusterDetails'],
        ]),
        metadata,
        defaultParams,
        tab: 'tab',
      }),
      gpuPage: Route.register<{
        id: string
        tab?: any
      }>({
        url: `${pluginRoutePrefix}/infrastructure/gpu/:tab`,
        name: 'GPU:Overview',
        id: 'GPU',
        breadcrumbs: new Map([['GPU', 'GPU']]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: IGpuPageTabs.Hosts,
        },
        tab: 'tab',
      }),
      floatingIps: Route.register({
        url: `${pluginRoutePrefix}/networking/public-ips`,
        name: 'Public IPs',
        id: 'publicIps',
        breadcrumbs: new Map([['public-IPs', 'publicIps']]),
        metadata,
        defaultParams,
      }),
      createFloatingIp: Route.register({
        url: `${pluginRoutePrefix}/networking/public-ips/create`,
        name: 'Public IPs:Create',
        breadcrumbs: new Map([
          ['public-IPs', 'publicIps'],
          ['create', 'publicIps'],
        ]),
        metadata,
        defaultParams,
      }),
      securityGroups: Route.register({
        url: `${pluginRoutePrefix}/networking/security-groups`,
        name: 'Security Groups',
        id: 'securityGroups',
        breadcrumbs: new Map([['security-groups', 'securityGroups']]),
        metadata,
        defaultParams,
      }),
      createSecurityGroup: Route.register({
        url: `${pluginRoutePrefix}/networking/security-groups/create`,
        name: 'Security Groups:Create',
        breadcrumbs: new Map([
          ['security-groups', 'securityGroups'],
          ['create', 'securityGroups'],
        ]),
        metadata,
        defaultParams,
      }),
      dns: Route.register({
        url: `${pluginRoutePrefix}/networking/dns/:tab`,
        name: 'DNS',
        id: 'dns',
        breadcrumbs: new Map([['DNS', 'dns']]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'pools',
          view: 'list',
        },
        tab: 'tab',
      }),
      createDnsResource: Route.register({
        url: `${pluginRoutePrefix}/networking/dns/:tab/create`,
        name: 'DNS',
        id: 'dns',
        breadcrumbs: new Map([['DNS', 'dns']]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'pools',
        },
      }),
      editDnsResource: Route.register({
        url: `${pluginRoutePrefix}/networking/dns/:tab/edit/:id`,
        name: 'DNS',
        id: 'dns',
        breadcrumbs: new Map([['DNS', 'dns']]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'pools',
        },
      }),
      loadBalancers: {
        details: Route.register<{
          tab?: ILoadBalancerDetailsPageTabs
        }>({
          id: 'loadBalancersDetails',
          url: `${pluginRoutePrefix}/networking/load-balancers/:tab`,
          breadcrumbs: new Map([['load-balancers', 'loadBalancersDetails']]),
          metadata,
          defaultParams: {
            ...defaultParams,
            tab: 'loadBalancers',
          },
          name: 'Load Balancers',
          tab: 'tab',
        }),
      },
      createLoadBalancers: Route.register<{
        tab?: ILoadBalancerDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/networking/load-balancers/:tab/create`,
        name: 'Load Balancer:Create',
        breadcrumbs: new Map([['load-balancer', 'loadBalancersDetails']]),
        metadata,
        defaultParams,
      }),

      stacks: Route.register({
        url: `${pluginRoutePrefix}/orchestration/stacks`,
        name: 'Stacks',
        id: 'stacks',
        breadcrumbs: new Map([['stacks', 'stacks']]),
        metadata,
        defaultParams,
      }),
      createStack: Route.register({
        url: `${pluginRoutePrefix}/orchestration/stacks/create`,
        name: 'Stacks:Create',
        breadcrumbs: new Map([
          ['stacks', 'stacks'],
          ['create', 'stacks'],
        ]),
        metadata,
        defaultParams,
      }),
      stackDetails: Route.register<{
        id: string
        name: string
        tab?: IStackDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/orchestration/stacks/:name/:id/:tab`,
        name: 'Stacks:Details',
        id: 'stackDetails',
        breadcrumbs: new Map([
          ['stacks', 'stacks'],
          [':name', 'stackDetails'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'overview',
        },
        tab: 'tab',
      }),
      appsDetails: {
        details: Route.register<{
          tab?: IAppsDetailsPageTabs
        }>({
          id: 'appsDetails',
          url: `${pluginRoutePrefix}/orchestration/apps/:tab`,
          breadcrumbs: new Map([['apps', 'appsDetails']]),
          metadata,
          defaultParams: {
            ...defaultParams,
            tab: 'myapps',
          },
          name: 'Apps',
          tab: 'tab',
        }),
      },
      createApps: Route.register<{
        tab?: IAppsDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/orchestration/apps/:tab/create`,
        name: 'Apps:Create',
        breadcrumbs: new Map([['apps', 'appsDetails']]),
        metadata,
        defaultParams,
      }),
      createTemplate: Route.register<{
        tab?: IAppsDetailsPageTabs
      }>({
        url: `${pluginRoutePrefix}/orchestration/apps/:tab/template`,
        name: 'Apps:template',
        breadcrumbs: new Map([['apps', 'appsDetails']]),
        metadata,
        defaultParams,
      }),
      repository: Route.register({
        url: `${pluginRoutePrefix}/orchestration/repository`,
        name: 'Git Repository',
        id: 'repository',
        breadcrumbs: new Map([['Git Repository', 'repository']]),
        metadata,
        defaultParams,
      }),
      createRepository: Route.register({
        url: `${pluginRoutePrefix}/orchestration/repository/create`,
        name: 'Git Repository:Create',
        breadcrumbs: new Map([
          ['Git Repository', 'repository'],
          ['create', 'repository'],
        ]),
        metadata,
        defaultParams,
      }),
      notifications: {
        list: Route.register<{ notificationType: string }>({
          url: `${pluginRoutePrefix}/notifications/:notificationType`,
          name: 'Notifications:List',
          metadata,
          defaultParams,
        }),
        detail: Route.register<{ notificationType: string; id: string }>({
          url: `${pluginRoutePrefix}/notifications/:notificationType/:id`,
          name: 'Notifications:Details',
          metadata,
          defaultParams,
        }),
      },
      help: Route.register({
        url: helpUrlDefinition,
        name: 'Help',
        metadata,
        defaultParams: {
          ...defaultParams,
          tab: 'support',
        },
      }),
      clusters: Route.register({
        url: `${pluginRoutePrefix}/infrastructure/clusters`,
        name: 'Clusters',
        id: 'clusters',
        breadcrumbs: new Map([['Clusters', 'clusters']]),
        metadata,
        defaultParams,
      }),
      createCluster: Route.register({
        url: `${pluginRoutePrefix}/infrastructure/clusters/create`,
        name: 'Clusters:Create',
        id: 'createCluster',
        breadcrumbs: new Map([['Clusters', 'clusters']]),
        metadata,
        defaultParams,
      }),
      clusterDetails: Route.register({
        url: `${pluginRoutePrefix}/infrastructure/clusters/:id`,
        name: 'Clusters:Details',
        id: 'clusterDetails',
        breadcrumbs: new Map([
          ['Clusters', 'clusters'],
          [':id', 'clusterDetails'],
        ]),
        metadata,
        defaultParams: {
          ...defaultParams,
        },
      }),
      health: {
        details: Route.register<{
          tab?: IHealthServiceDetailsPageTabs
        }>({
          url: `${pluginRoutePrefix}/health/:tab`,
          id: 'serviceHealth',
          name: 'Service Health',
          breadcrumbs: new Map([['Service Health', 'serviceHealth']]),
          metadata,
          defaultParams: {
            ...defaultParams,
            tab: 'serviceHealth',
          },
          tab: 'tab',
        }),
      },
    },
  }
}
